/**
 * FieldFeedbackForm.js
 * Public form for field team to submit verification responses
 * Accessed via shareable link - NO authentication required
 * Supports dark mode and handles summary issues appropriately
 */

import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  ChevronDown,
  ChevronUp,
  Edit3,
  Info,
  Loader,
  Check,
  X,
  Clock,
  AlertTriangle,
  MapPin,
  Copy,
  Layers,
  Zap,
  RefreshCw,
  MessageSquare,
  Link as LinkIcon,
  Target,
  Activity,
  User
} from 'lucide-react';

// API Base URL
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

// Issue type icons
const ISSUE_ICONS = {
  outlier: AlertTriangle,
  duplicate: Copy,
  missing: AlertCircle,
  similar_response: Layers,
  gps_issue: MapPin,
  duration: Clock,
  skip_logic: Zap,
  backcheck: RefreshCw,
  // Coaching-loop detectors (sentinel/gated/probe/straight-lining)
  sentinel_phrase: MessageSquare,
  gated_field:     LinkIcon,
  probe_depth:     Target,
  straight_lining: Activity
};

const FieldFeedbackForm = ({ sessionId, isDarkMode: propIsDarkMode }) => {
  // Theme - use prop if provided, otherwise follow system preference
  const [systemDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  
  const isDarkMode = propIsDarkMode !== undefined ? propIsDarkMode : systemDarkMode;
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const [expandedIssues, setExpandedIssues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  // Ship 3: per-issue server-side validation errors keyed by issue.id.
  // Populated when /respond returns 422 with detail.field_errors.
  const [fieldErrors, setFieldErrors] = useState({});
  const [supervisorName, setSupervisorName] = useState('');
  const [supervisorContact, setSupervisorContact] = useState('');
  const [nameError, setNameError] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30; // Smaller for mobile
  
  // Colors based on theme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F3F4F6',
    cardBg: isDarkMode ? '#17171B' : '#FFFFFF',
    border: isDarkMode ? '#2D2E32' : '#E5E7EB',
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    inputBg: isDarkMode ? '#1F1F23' : '#FFFFFF',
    headerBg: isDarkMode ? '#17171B' : '#FFFFFF',
    primary: '#6366F1',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    error: '#EF4444',
    info: '#2563EB',
    purple: '#8B5CF6'
  };
  
  // Severity colors (work in both modes)
  const getSeverityColors = (severity) => {
    const severityMap = {
      critical: { 
        bg: isDarkMode ? '#7F1D1D' : '#FEE2E2', 
        text: isDarkMode ? '#FCA5A5' : '#DC2626', 
        border: isDarkMode ? '#991B1B' : '#FECACA' 
      },
      warning: { 
        bg: isDarkMode ? '#78350F' : '#FEF3C7', 
        text: isDarkMode ? '#FCD34D' : '#D97706', 
        border: isDarkMode ? '#92400E' : '#FDE68A' 
      },
      info: { 
        bg: isDarkMode ? '#1E3A5F' : '#DBEAFE', 
        text: isDarkMode ? '#93C5FD' : '#1A6FE0', 
        border: isDarkMode ? '#1E40AF' : '#BFDBFE' 
      }
    };
    return severityMap[severity] || { bg: colors.cardBg, text: colors.text, border: colors.border };
  };
  
  // Check if an issue is a summary (not actionable for correction)
  const isSummaryIssue = (issue) => {
    return issue.row_index === null || 
           issue.id?.includes('_summary') ||
           (issue.respondent_id && (
             issue.respondent_id.includes('outliers') ||
             issue.respondent_id.includes('interviews') ||
             issue.respondent_id.includes('values') ||
             issue.respondent_id.includes('records') ||
             issue.respondent_id.includes('pairs') ||
             issue.respondent_id.includes('responses') ||
             issue.respondent_id.includes('violations') ||
             issue.respondent_id.includes('variables')
           ));
  };
  
  // Fetch session data on mount
  useEffect(() => {
    const fetchSession = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch(`${API_BASE}/api/verification/public/${sessionId}`);
        
        if (response.status === 404) {
          throw new Error('Verification link not found. It may have been deleted.');
        }
        if (response.status === 410) {
          throw new Error('This verification link has expired.');
        }
        if (!response.ok) {
          throw new Error('Failed to load verification data');
        }
        
        const data = await response.json();
        setSessionData(data);
        
        // Initialize responses from existing data
        const existingResponses = {};
        data.issues.forEach(issue => {
          if (issue.response_status && issue.response_status !== 'pending') {
            existingResponses[issue.id] = {
              status: issue.response_status,
              corrected_value: issue.corrected_value,
              notes: issue.notes
            };
          }
        });
        setResponses(existingResponses);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId]);
  
  // Handle response for an issue
  const handleResponse = (issueId, status, correctedValue = null, notes = null) => {
    setResponses(prev => ({
      ...prev,
      [issueId]: {
        status,
        corrected_value: correctedValue,
        notes
      }
    }));
  };
  
  // Submit all responses
  const handleSubmit = async () => {
    // Validate name is provided
    if (!supervisorName.trim()) {
      setNameError(true);
      return;
    }
    setNameError(false);
    
    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});  // Ship 3: clear stale per-issue errors before retry
    
    try {
      const responsesPayload = Object.entries(responses).map(([issueId, data]) => ({
        issue_id: issueId,
        status: data.status,
        corrected_value: data.corrected_value,
        notes: data.notes
      }));
      
      const response = await fetch(`${API_BASE}/api/verification/public/${sessionId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses: responsesPayload,
          supervisor_name: supervisorName.trim(),
          supervisor_contact: supervisorContact || null
        })
      });
      
      if (!response.ok) {
        // Ship 3: 422 means correction validation failed — surface per-issue
        // errors inline on each affected input rather than blocking the whole
        // submit with a generic toast.
        if (response.status === 422) {
          let payload = null;
          try { payload = await response.json(); } catch { /* not JSON */ }
          const fe =
            payload?.detail?.field_errors ||
            payload?.field_errors ||
            null;
          if (fe && typeof fe === 'object') {
            setFieldErrors(fe);
            const count = Object.keys(fe).length;
            setError(
              `${count} correction${count !== 1 ? 's' : ''} failed validation. ` +
              `Fix the highlighted field${count !== 1 ? 's' : ''} below and try again.`
            );
            // Do NOT throw — staying on the page lets the supervisor fix and resubmit.
            return;
          }
        }
        throw new Error('Failed to submit responses');
      }
      
      const result = await response.json();
      setSubmitResult(result);
      setSubmitted(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Toggle issue expansion
  const toggleExpand = (issueId) => {
    setExpandedIssues(prev => ({
      ...prev,
      [issueId]: !prev[issueId]
    }));
  };
  
  // Calculate progress
  const summaryIssues = sessionData?.issues?.filter(i => isSummaryIssue(i)) || [];
  const completedCount = Object.keys(responses).length;
  const totalCount = sessionData?.issues?.length || 0;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  
  // Loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '40px',
        backgroundColor: colors.background
      }}>
        <Loader size={48} style={{ color: colors.textSecondary, animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '16px', color: colors.textSecondary }}>Loading verification data...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  
  // Error state
  if (error && !sessionData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: colors.background
      }}>
        <AlertCircle size={64} style={{ color: colors.danger, marginBottom: '12px' }} />
        <h2 style={{ color: colors.text, marginBottom: '8px' }}>Unable to Load</h2>
        <p style={{ color: colors.textSecondary, maxWidth: '300px' }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '24px',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: colors.info,
            color: '#FFF',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  // Success state
  if (submitted) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: isDarkMode ? '#064E3B' : '#ECFDF5'
      }}>
        <CheckCircle size={80} style={{ color: colors.success, marginBottom: '12px' }} />
        <h2 style={{ color: colors.text, marginBottom: '8px', fontSize: '20px' }}>
          Thank You!
        </h2>
        <p style={{ color: colors.textSecondary, maxWidth: '300px', marginBottom: '12px' }}>
          Your responses have been submitted successfully.
          {submitResult?.updated_count && (
            <><br /><strong>{submitResult.updated_count}</strong> issues updated.</>
          )}
        </p>
        {submitResult?.summary && (
          <div style={{
            backgroundColor: colors.cardBg,
            padding: '16px 24px',
            borderRadius: '12px',
            textAlign: 'left',
            marginBottom: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <p style={{ margin: '4px 0', fontSize: '13px', color: colors.text }}>
              ✓ Valid: <strong>{submitResult.summary.valid}</strong>
            </p>
            <p style={{ margin: '4px 0', fontSize: '13px', color: colors.text }}>
              ✎ Corrected: <strong>{submitResult.summary.corrected}</strong>
            </p>
            <p style={{ margin: '4px 0', fontSize: '13px', color: colors.text }}>
              ? Needs Info: <strong>{submitResult.summary.needs_info}</strong>
            </p>
            <p style={{ margin: '4px 0', fontSize: '13px', color: colors.text }}>
              ○ Pending: <strong>{submitResult.summary.pending}</strong>
            </p>
          </div>
        )}
        <button
          onClick={() => {
            setSubmitted(false);
            window.location.reload();
          }}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: colors.info,
            color: '#FFF',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Review & Update More
        </button>
      </div>
    );
  }
  
  // Main form
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.background,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      transition: 'background-color 0.3s ease'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: colors.headerBg,
        padding: '16px 20px',
        borderBottom: `1px solid ${colors.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '13px', fontWeight: '600', color: colors.text, margin: 0 }}>
              Data Verification
            </h1>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '4px 0 0 0' }}>
              {sessionData?.project_name}
            </p>
            <p style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
              {sessionData?.days_remaining} days remaining
              {sessionData?.collector_filter && ` • Collector: ${sessionData.collector_filter}`}
            </p>
          </div>
        </div>
        <div style={{
          height: '4px',
          backgroundColor: colors.border,
          borderRadius: '2px',
          marginTop: '12px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            backgroundColor: colors.success,
            width: `${progress}%`,
            transition: 'width 0.3s ease'
          }} />
        </div>
        <p style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
          {completedCount} of {totalCount} responded
          {summaryIssues.length > 0 && ` • ${summaryIssues.length} summary items`}
        </p>
      </div>
      
      {/* Content */}
      <div style={{ padding: '16px', paddingBottom: '100px' }}>
        {/* Message from manager */}
        {sessionData?.message_from_manager && (
          <div style={{
            backgroundColor: isDarkMode ? '#1E3A5F' : '#EFF6FF',
            border: `1px solid ${isDarkMode ? '#1E40AF' : '#BFDBFE'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '12px',
            fontSize: '13px',
            color: isDarkMode ? '#93C5FD' : '#1E40AF'
          }}>
            <strong>Note:</strong> {sessionData.message_from_manager}
          </div>
        )}
        
        {/* Show when issues have been archived/cleared by data manager */}
        {sessionData?.archived_count > 0 && (
          <div style={{
            backgroundColor: isDarkMode ? '#1A2F1A' : '#ECFDF5',
            border: `1px solid ${isDarkMode ? '#166534' : '#A7F3D0'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '12px',
            fontSize: '13px',
            color: isDarkMode ? '#86EFAC' : '#166534',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '13px' }}>✓</span>
            <span>
              <strong>{sessionData.archived_count} issue{sessionData.archived_count > 1 ? 's' : ''} resolved</strong> and cleared by data manager.
              {sessionData.total_issues > 0 && ` ${sessionData.total_issues} remaining.`}
            </span>
          </div>
        )}
        
        {/* Stats */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
          <div style={{
            padding: '8px 16px',
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            fontSize: '13px',
            whiteSpace: 'nowrap',
            border: `1px solid ${colors.border}`,
            color: colors.text
          }}>
            <span style={{ color: colors.danger }}>●</span> {sessionData?.issues?.filter(i => i.severity === 'critical').length || 0} Critical
          </div>
          <div style={{
            padding: '8px 16px',
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            fontSize: '13px',
            whiteSpace: 'nowrap',
            border: `1px solid ${colors.border}`,
            color: colors.text
          }}>
            <span style={{ color: colors.warning }}>●</span> {sessionData?.issues?.filter(i => i.severity === 'warning').length || 0} Warning
          </div>
          <div style={{
            padding: '8px 16px',
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            fontSize: '13px',
            whiteSpace: 'nowrap',
            border: `1px solid ${colors.border}`,
            color: colors.text
          }}>
            <span style={{ color: colors.info }}>●</span> {sessionData?.issues?.filter(i => i.severity === 'info').length || 0} Info
          </div>
        </div>
        
        {/* Summary issues notice */}
        {summaryIssues.length > 0 && (
          <div style={{
            backgroundColor: isDarkMode ? '#374151' : '#F3F4F6',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '12px',
            fontSize: '13px',
            color: colors.textSecondary
          }}>
            <strong>Note:</strong> {summaryIssues.length} items show summary counts without specific row details. 
            These can be marked as "Acknowledged" or "Need Info" but cannot be corrected without knowing which specific record.
          </div>
        )}
        
        {/* Supervisor Info (optional) */}
        <div style={{
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <User size={18} style={{ color: colors.textSecondary }} />
            <span style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>Your Information</span>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <input
              type="text"
              placeholder="Your name *"
              value={supervisorName}
              onChange={(e) => {
                setSupervisorName(e.target.value);
                if (nameError && e.target.value.trim()) {
                  setNameError(false);
                }
              }}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${nameError ? colors.error : colors.border}`,
                backgroundColor: colors.inputBg,
                color: colors.text,
                fontSize: '13px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            {nameError && (
              <p style={{ fontSize: '12px', color: colors.error, marginTop: '4px', marginBottom: '0' }}>
                Please enter your name
              </p>
            )}
          </div>
          <input
            type="text"
            placeholder="Phone or email (optional)"
            value={supervisorContact}
            onChange={(e) => setSupervisorContact(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.inputBg,
              color: colors.text,
              fontSize: '13px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        {/* Issues */}
        {(() => {
          // Calculate pagination
          const allIssues = sessionData?.issues || [];
          const totalIssues = allIssues.length;
          const totalPages = Math.ceil(totalIssues / ITEMS_PER_PAGE);
          const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
          const paginatedIssues = allIssues.slice(startIndex, startIndex + ITEMS_PER_PAGE);
          
          if (totalIssues === 0) {
            return (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: colors.cardBg,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#10B98120',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <CheckCircle size={40} style={{ color: '#10B981' }} />
                </div>
                <h3 style={{ color: colors.text, fontSize: '20px', marginBottom: '8px' }}>
                  All Done! 🎉
                </h3>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
                  {sessionData?.archived_count > 0 
                    ? `All ${sessionData.archived_count} issues have been reviewed and resolved.`
                    : 'No issues require verification at this time.'}
                </p>
                <p style={{ color: colors.textMuted, fontSize: '13px' }}>
                  Thank you for your responses. The data team has been notified.
                </p>
              </div>
            );
          }
          
          return (
            <>
              {/* Pagination Info */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  backgroundColor: colors.cardBg,
                  borderRadius: '8px',
                  marginBottom: '12px',
                  border: `1px solid ${colors.border}`
                }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: currentPage === 1 ? colors.border : colors.primary,
                      color: currentPage === 1 ? colors.textMuted : '#FFF',
                      fontSize: '13px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ← Prev
                  </button>
                  <span style={{ color: colors.text, fontSize: '13px', fontWeight: '500' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: currentPage === totalPages ? colors.border : colors.primary,
                      color: currentPage === totalPages ? colors.textMuted : '#FFF',
                      fontSize: '13px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
              
              {/* Paginated Issues */}
              {paginatedIssues.map((issue, index) => {
          const Icon = ISSUE_ICONS[issue.type] || AlertCircle;
          const severity = getSeverityColors(issue.severity);
          const isExpanded = expandedIssues[issue.id];
          const currentResponse = responses[issue.id];
          const hasResponse = !!currentResponse;
          const isSummary = isSummaryIssue(issue);
          
          return (
            <div 
              key={issue.id} 
              style={{
                backgroundColor: colors.cardBg,
                borderRadius: '12px',
                marginBottom: '12px',
                overflow: 'hidden',
                boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
                border: `1px solid ${colors.border}`,
                borderLeft: `4px solid ${severity.border}`,
                opacity: hasResponse ? 0.85 : 1
              }}
            >
              <div 
                style={{
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  cursor: 'pointer'
                }}
                onClick={() => toggleExpand(issue.id)}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  backgroundColor: severity.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={18} style={{ color: severity.text }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '4px' }}>
                    <span style={{ color: colors.textMuted, marginRight: '8px' }}>#{index + 1}</span>
                    {issue.variable}
                    {issue.row_index !== null && (
                      <span style={{ fontSize: '12px', color: colors.textMuted, marginLeft: '8px' }}>
                        Row {issue.row_index}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: colors.textSecondary }}>{issue.description}</div>
                  
                  {/* Show respondent/collector info if available and not summary */}
                  {!isSummary && (issue.respondent_id || issue.collector_id) && (
                    <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
                      {issue.respondent_id && <span>ID: {issue.respondent_id}</span>}
                      {issue.respondent_id && issue.collector_id && <span> • </span>}
                      {issue.collector_id && <span>Collector: {issue.collector_id}</span>}
                    </div>
                  )}
                  
                  {/* Summary badge */}
                  {isSummary && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      backgroundColor: isDarkMode ? '#374151' : '#F3F4F6',
                      borderRadius: '8px',
                      fontSize: '11px',
                      color: colors.textSecondary,
                      marginTop: '8px'
                    }}>
                      <Info size={12} />
                      Summary - no specific row
                    </div>
                  )}
                  
                  {hasResponse && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '8px',
                      marginLeft: isSummary ? '8px' : '0',
                      padding: '4px 8px',
                      backgroundColor: currentResponse.status === 'valid' ? (isDarkMode ? '#0FC0C0' : '#ECFDF5') : 
                                       currentResponse.status === 'corrected' ? (isDarkMode ? '#5B21B6' : '#EDE9FE') : 
                                       (isDarkMode ? '#78350F' : '#FEF3C7'),
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: currentResponse.status === 'valid' ? colors.success : 
                             currentResponse.status === 'corrected' ? colors.purple : colors.warning
                    }}>
                      {currentResponse.status === 'valid' ? <Check size={12} /> : 
                       currentResponse.status === 'corrected' ? <Edit3 size={12} /> : <Info size={12} />}
                      {currentResponse.status === 'valid' ? (isSummary ? 'Acknowledged' : 'Marked Valid') : 
                       currentResponse.status === 'corrected' ? 'Corrected' : 'Needs Info'}
                    </div>
                  )}
                </div>
                <div>
                  {isExpanded ? <ChevronUp size={20} style={{ color: colors.textMuted }} /> : 
                               <ChevronDown size={20} style={{ color: colors.textMuted }} />}
                </div>
              </div>
              
              {isExpanded && (
                <div style={{
                  padding: '0 16px 16px 16px',
                  borderTop: `1px solid ${colors.border}`
                }}>
                  {/* Current value */}
                  <div style={{
                    fontSize: '12px',
                    color: colors.text,
                    backgroundColor: isDarkMode ? '#1F1F23' : '#F3F4F6',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    marginTop: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all'
                  }}>
                    <strong>Current Value:</strong> {String(issue.current_value ?? 'N/A')}
                  </div>
                  
                  {/* Details */}
                  {issue.details && (
                    <p style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '8px' }}>
                      {issue.details}
                    </p>
                  )}
                  
                  {/* Response buttons */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleResponse(issue.id, 'valid')}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: currentResponse?.status === 'valid' ? colors.success : (isDarkMode ? '#0FC0C0' : '#ECFDF5'),
                        color: currentResponse?.status === 'valid' ? '#FFF' : colors.success
                      }}
                    >
                      <Check size={16} />
                      {isSummary ? 'Acknowledged' : 'Valid'}
                    </button>
                    
                    {/* Only show Correct button for non-summary issues */}
                    {!isSummary && (
                      <button
                        onClick={() => handleResponse(issue.id, 'corrected')}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '8px',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          backgroundColor: currentResponse?.status === 'corrected' ? colors.purple : (isDarkMode ? '#5B21B6' : '#EDE9FE'),
                          color: currentResponse?.status === 'corrected' ? '#FFF' : colors.purple
                        }}
                      >
                        <Edit3 size={16} />
                        Correct
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleResponse(issue.id, 'needs_info')}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: currentResponse?.status === 'needs_info' ? colors.warning : (isDarkMode ? '#78350F' : '#FEF3C7'),
                        color: currentResponse?.status === 'needs_info' ? '#FFF' : colors.warning
                      }}
                    >
                      <Info size={16} />
                      Need Info
                    </button>
                  </div>
                  
                  {/* Correction input - only for non-summary issues
                      Ship 3: smart input rendering based on issue metadata.
                        - issue.choices present       → <select>
                        - issue.column_type='numeric' → <input type="number"> with min/max
                        - issue.column_type ∈ {date,datetime,timestamp} → date input
                        - default                     → text input (pre-Ship-3 behavior)
                      Server-side validation errors land in fieldErrors[issue.id]. */}
                  {!isSummary && currentResponse?.status === 'corrected' && (() => {
                    const baseInputStyle = {
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: `1px solid ${fieldErrors[issue.id] ? colors.danger : colors.border}`,
                      backgroundColor: colors.inputBg,
                      color: colors.text,
                      fontSize: '13px',
                      marginTop: '12px',
                      boxSizing: 'border-box'
                    };

                    const onChangeHandler = (e) => {
                      // Ship 3: clear stale server-side error for this issue
                      // as soon as the supervisor edits, so the red warning
                      // doesn't linger after they fix the value.
                      if (fieldErrors[issue.id]) {
                        setFieldErrors(prev => {
                          const next = { ...prev };
                          delete next[issue.id];
                          return next;
                        });
                      }
                      handleResponse(issue.id, 'corrected', e.target.value);
                    };

                    const value = currentResponse?.corrected_value ?? '';

                    let input;

                    // 1. Choice list → <select> with all valid options + an
                    //    empty default. Don't auto-select the current bad value
                    //    so the supervisor has to consciously pick a real choice.
                    if (Array.isArray(issue.choices) && issue.choices.length > 0) {
                      input = (
                        <select
                          value={value}
                          onChange={onChangeHandler}
                          style={baseInputStyle}
                          autoFocus
                        >
                          <option value="">— select correct value —</option>
                          {issue.choices.map((c, i) => (
                            <option key={`${c.value}-${i}`} value={c.value}>
                              {c.label && c.label !== c.value ? `${c.label} (${c.value})` : c.value}
                            </option>
                          ))}
                          <option value="">(blank — set to missing)</option>
                        </select>
                      );
                    }
                    // 2. Numeric → number input with min/max attrs (browser nudges the user)
                    else if (
                      issue.column_type &&
                      ['numeric', 'integer', 'float', 'int', 'number', 'decimal'].includes(
                        String(issue.column_type).toLowerCase()
                      )
                    ) {
                      const minAttr = issue.min_value != null ? { min: issue.min_value } : {};
                      const maxAttr = issue.max_value != null ? { max: issue.max_value } : {};
                      input = (
                        <input
                          type="number"
                          step="any"
                          {...minAttr}
                          {...maxAttr}
                          placeholder={
                            issue.min_value != null && issue.max_value != null
                              ? `Enter a number between ${issue.min_value} and ${issue.max_value}…`
                              : 'Enter the correct number…'
                          }
                          value={value}
                          onChange={onChangeHandler}
                          style={baseInputStyle}
                          autoFocus
                        />
                      );
                    }
                    // 3. Date / datetime
                    else if (
                      issue.column_type &&
                      ['date', 'datetime', 'timestamp', 'time'].includes(
                        String(issue.column_type).toLowerCase()
                      )
                    ) {
                      const isDateTime =
                        ['datetime', 'timestamp'].includes(String(issue.column_type).toLowerCase());
                      // Browser date inputs want strict YYYY-MM-DD / YYYY-MM-DDTHH:MM.
                      // Strip any fractional seconds / timezone for the control's
                      // value; the supervisor still sees the original via current_value.
                      const cleanValue =
                        typeof value === 'string'
                          ? (isDateTime ? value.slice(0, 16) : value.slice(0, 10))
                          : '';
                      input = (
                        <input
                          type={isDateTime ? 'datetime-local' : 'date'}
                          value={cleanValue}
                          onChange={onChangeHandler}
                          style={baseInputStyle}
                          autoFocus
                        />
                      );
                    }
                    // 4. Default — free text
                    else {
                      input = (
                        <input
                          type="text"
                          placeholder="Enter the correct value..."
                          value={value}
                          onChange={onChangeHandler}
                          style={baseInputStyle}
                          autoFocus
                        />
                      );
                    }

                    return (
                      <>
                        {input}
                        {/* XLSForm constraint hint (display only — not enforced client-side) */}
                        {issue.constraint_message && !fieldErrors[issue.id] && (
                          <p style={{
                            fontSize: '11px',
                            color: colors.textMuted,
                            marginTop: '6px',
                            fontStyle: 'italic'
                          }}>
                            ⓘ {issue.constraint_message}
                          </p>
                        )}
                        {/* Server-side validation error from a 422 response */}
                        {fieldErrors[issue.id] && (
                          <p style={{
                            fontSize: '12px',
                            color: colors.danger,
                            marginTop: '6px',
                            fontWeight: 500
                          }}>
                            ⚠ {fieldErrors[issue.id]}
                          </p>
                        )}
                      </>
                    );
                  })()}
                  
                  {/* Notes input */}
                  {currentResponse?.status === 'needs_info' && (
                    <input
                      type="text"
                      placeholder="What information do you need?"
                      value={currentResponse?.notes || ''}
                      onChange={(e) => handleResponse(issue.id, 'needs_info', null, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.inputBg,
                        color: colors.text,
                        fontSize: '13px',
                        marginTop: '12px',
                        boxSizing: 'border-box'
                      }}
                      autoFocus
                    />
                  )}
                  
                  {/* Help text for summary issues */}
                  {isSummary && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: colors.textMuted, 
                      marginTop: '12px',
                      fontStyle: 'italic' 
                    }}>
                      This is a summary of multiple records. To correct specific values, 
                      ask the data manager to run detailed quality checks.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
              
              {/* Bottom Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '16px',
                  gap: '16px'
                }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: currentPage === 1 ? colors.border : colors.primary,
                      color: currentPage === 1 ? colors.textMuted : '#FFF',
                      fontSize: '13px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ← Previous
                  </button>
                  <span style={{ color: colors.text }}>{currentPage} / {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: currentPage === totalPages ? colors.border : colors.primary,
                      color: currentPage === totalPages ? colors.textMuted : '#FFF',
                      fontSize: '13px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>
      
      {/* Footer with submit button */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '16px 20px',
        backgroundColor: colors.headerBg,
        borderTop: `1px solid ${colors.border}`,
        boxShadow: isDarkMode ? 'none' : '0 -4px 12px rgba(0,0,0,0.1)'
      }}>
        {error && (
          <div style={{
            backgroundColor: isDarkMode ? '#7F1D1D' : '#FEE2E2',
            color: isDarkMode ? '#FCA5A5' : '#DC2626',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            marginBottom: '12px'
          }}>
            {error}
          </div>
        )}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || completedCount === 0}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: colors.success,
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            cursor: (isSubmitting || completedCount === 0) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: (isSubmitting || completedCount === 0) ? 0.7 : 1
          }}
        >
          {isSubmitting ? (
            <>
              <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Submitting...
            </>
          ) : (
            <>
              <Send size={20} />
              Submit {completedCount} Response{completedCount !== 1 ? 's' : ''}
            </>
          )}
        </button>
      </div>
      
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default FieldFeedbackForm;